/**
 * Answer Feedback Component
 *
 * Shows the result of the player's answer with points and ranking.
 */

interface QuestionOption {
  id: string;
  value: string;
  label: string;
  color: string;
}

interface AnswerFeedbackProps {
  correct: boolean;
  points: number;
  totalScore: number;
  streak: number;
  rank: number;
  totalParticipants: number;
  correctAnswer?: unknown;
  myAnswer?: unknown;
  streakThreshold?: number;
  questionOptions?: QuestionOption[];
}

export function AnswerFeedback({
  correct,
  points,
  totalScore,
  streak,
  rank,
  totalParticipants: _totalParticipants,
  correctAnswer,
  myAnswer: _myAnswer,
  streakThreshold = 3,
  questionOptions,
}: AnswerFeedbackProps) {
  // totalParticipants and myAnswer are available for future display enhancements
  void _totalParticipants;
  void _myAnswer;
  const showStreakBonus = correct && streak >= streakThreshold;

  // Convert correctAnswer from option ID to label if options are available
  const getCorrectAnswerDisplay = () => {
    if (!correctAnswer) return undefined;

    // If we have options, try to find the matching option by ID or value
    if (questionOptions && questionOptions.length > 0) {
      const matchingOption = questionOptions.find(
        opt => opt.id === String(correctAnswer) || opt.value === String(correctAnswer)
      );
      if (matchingOption) {
        return matchingOption.label;
      }
    }

    // Fallback to the raw value
    return String(correctAnswer);
  };

  const correctAnswerDisplay = getCorrectAnswerDisplay();

  return (
    <div className="text-center max-w-md w-full">
      {/* Result Icon */}
      <div
        className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center ${
          correct
            ? 'bg-green-500 shadow-lg shadow-green-500/50'
            : 'bg-red-500 shadow-lg shadow-red-500/50'
        }`}
      >
        {correct ? (
          <svg className="w-14 h-14 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-14 h-14 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        )}
      </div>

      {/* Message */}
      <h2 className={`text-3xl font-bold mb-4 ${correct ? 'text-green-400' : 'text-red-400'}`}>
        {correct ? 'Correct!' : 'Wrong!'}
      </h2>

      {/* Points */}
      <div className={`text-6xl font-bold mb-4 ${correct ? 'text-green-300' : 'text-gray-400'}`}>
        +{points}
      </div>

      {/* Streak Bonus */}
      {showStreakBonus && (
        <div className="flex items-center justify-center gap-2 mb-6 animate-bounce">
          <span className="text-3xl">🔥</span>
          <span className="text-xl font-bold text-orange-400">
            {streak} streak bonus!
          </span>
          <span className="text-3xl">🔥</span>
        </div>
      )}

      {/* Correct Answer (if wrong) */}
      {!correct && correctAnswerDisplay && (
        <div className="p-4 bg-white/10 rounded-xl mb-6">
          <p className="text-sm text-purple-300 mb-1">Correct answer:</p>
          <p className="text-lg font-bold text-white">{correctAnswerDisplay}</p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 p-4 bg-white/5 rounded-xl">
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{totalScore.toLocaleString()}</p>
          <p className="text-xs text-purple-400">Total Score</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white">#{rank}</p>
          <p className="text-xs text-purple-400">Rank</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{streak}</p>
          <p className="text-xs text-purple-400">Streak</p>
        </div>
      </div>

      {/* Position Message */}
      <p className="mt-6 text-purple-300">
        {rank === 1 && "You're in the lead! 🏆"}
        {rank === 2 && "So close to first place! Keep going!"}
        {rank === 3 && "You're on the podium! 🥉"}
        {rank > 3 && rank <= 10 && "Great job! Keep climbing!"}
        {rank > 10 && "There's still time to catch up!"}
      </p>
    </div>
  );
}

export default AnswerFeedback;
